import React, { useState, useEffect } from "react";
import useMediaQuery from "hooks/useMediaQuery";
import { BreakPoints } from "utils/BreakPoints";
import styled from "styled-components";
import PropTypes from "prop-types";
import amplitude from "amplitude-js";
import { IoCallOutline } from "react-icons/io5";

interface LinkProps {
  readonly backgroundColor: string;
  readonly textColor: string;
  readonly variation?: boolean;
  hasNumber?: boolean;
}
interface PhoneNumberProps {
  readonly variation?: boolean;
}
interface HeroCtaTextProps {
  readonly variation?: boolean;
}

const CtaLink = styled.a<LinkProps>`
  display: flex;
  height: 56px;
  width: auto;
  border-radius: 4px;
  text-decoration: none;
  background: ${(props) => props.backgroundColor};
  color: ${(props) => props.textColor};
  font-size: ${(props) => (props.hasNumber ? "10px" : "16px")};
  line-height: 100%;
  text-transform: uppercase;
  justify-content: center;
  padding: 0 20px;

  @media (max-width: 768px) {
    margin-top: 2px;
    margin-left: 5px;
    height: 48px;
  }
`;

const HeroCtaIcon = styled.div`
  width: auto;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    width: 40px;
  }
`;

const HeroCtaText = styled.div<HeroCtaTextProps>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: left;
  text-transform: uppercase;
  padding: ${(props) => (props.variation ? "0" : "0 20px")};
  font-weight: 600;
  padding-left: 10px;

  @media (max-width: 768px) {
    padding: 0 12px;
    white-space: nowrap;
    padding-left: 0;
  }
`;

const HeroCtaNumber = styled.span<PhoneNumberProps>`
  font-size: ${(props) => (props.variation ? "16px" : "16px")};
  line-height: ${(props) => (props.variation ? "16px" : "20px")};
  padding-top: ${(props) => (props.variation ? "0" : "4px")};
  font-weight: 700;
  text-transform: uppercase;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

function HeaderCallCta({
  text = "Free confidential call",
  phoneText = "",
  ctaPhoneLink = "",
  backgroundColor = "",
  separatorColor = "",
  textColor = "#fff",
  variation = false,
  hasNumber = true,
  component = "",
  website = "",
}): JSX.Element {
  const isTabletBreakPoint = useMediaQuery(BreakPoints.MOBILE);
  const [isTablet, setTablet] = useState(false);

  useEffect(() => {
    if (isTabletBreakPoint) {
      setTablet(true);
    } else {
      setTablet(false);
    }
  }, [isTabletBreakPoint]);

  const onClickHandler = (info: any) => {
    const API_KEY = process.env.REACT_APP_AMPLITUDE_API_KEY;
    const PAGE = process.env.REACT_APP_WEBSITE;
    if (API_KEY && PAGE) {
      amplitude.getInstance().init(API_KEY);
      const event = "LandingPageButtonClick";
      const data = {
        page: "Home Page",
        website: website ? website : PAGE,
        action: "Button Click",
        text: info,
        component: component,
      };
      amplitude.getInstance().logEvent(event, data);
    }
  };

  return (
    <>
      <CtaLink
        hasNumber={hasNumber}
        backgroundColor={backgroundColor}
        textColor={textColor}
        href={ctaPhoneLink}
        onClick={() => onClickHandler(text)}
      >
        {hasNumber && !isTablet ? (
          <>
            <HeroCtaIcon>
              <IoCallOutline size={32} />
            </HeroCtaIcon>
            <HeroCtaText>
              <span>{text}</span>
              <HeroCtaNumber>{phoneText}</HeroCtaNumber>
            </HeroCtaText>
          </>
        ) : (
          <>
            <HeroCtaIcon>
              <IoCallOutline size={20} />
            </HeroCtaIcon>
            <HeroCtaText>
              <span>{text}</span>
            </HeroCtaText>
          </>
        )}
      </CtaLink>
    </>
  );
}

HeaderCallCta.propTypes = {
  text: PropTypes.string,
  phoneText: PropTypes.string,
  ctaPhoneLink: PropTypes.string,
  backgroundColor: PropTypes.string,
  separatorColor: PropTypes.string,
  textColor: PropTypes.string,
  variation: PropTypes.bool,
  website: PropTypes.string,
};

export default HeaderCallCta;
